import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Text,
  Stack,
  Loader,
  Group,
  Button,
  Alert,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import CardPaymentSuccessPanel from "./CardPaymentSuccessPanel";
import { bnplMethodForKey } from "../../config/bnpl";

/**
 * Single modal, three workflows (switches: `cardOnFile`, `paymentMethod`).
 *
 * **Terminal / in-person card (`cardOnFile === false`, `paymentMethod === "card"`)** — default
 * - `processPayment` drives the WisePOS reader, then we poll until the intent settles.
 *
 * **Terminal BNPL QR (`paymentMethod === "affirm"`)**
 * - Same reader handoff, but the reader displays a QR code and the customer
 *   completes the financing application on their own phone — no on-reader
 *   tipping, a much longer polling window (applications take minutes), and
 *   retry only re-drives the reader while the QR hasn't been scanned yet.
 *
 * **Card on file (`cardOnFile === true`)**
 * - Charge was already started via `chargeCardOnFile` before this modal opens.
 * - We only poll until the intent settles (no reader, no `processPayment`).
 *
 * **Shared (all workflows)** — same UI for the overlap:
 * - Polling + status handling, success handoff via `onTransactionUpdated`
 * - `CardPaymentSuccessPanel` (receipt, allocate tips) when status is succeeded
 * - Refetch-on-close when tips were edited
 */
// A busy reader means another checkout is using that terminal — steer the user
// to a different one now that a location can have several.
const friendlyTerminalError = (message) =>
  message && /busy/i.test(message)
    ? "That terminal is busy with another checkout. Wait for it to finish, or pick a different terminal."
    : message;

function PaymentProcessingModal({
  opened,
  onClose,
  transactionId,
  paymentIntentId,
  eligibleAmountForTip,
  terminalDeviceId,
  customerEmail,
  onTransactionUpdated,
  transaction,
  onRefetchTransaction,
  cardOnFile = false,
  paymentMethod = "card",
}) {
  const { businessInfo } = useSalon();
  const {
    processPayment,
    pollStripePaymentStatus,
    simulatePayment,
    cancelPaymentIntent,
  } = useApp();
  // The checkout picker passes the chosen terminal; fall back to the legacy
  // single-device field for any caller that hasn't been updated yet.
  const effectiveTerminalDeviceId =
    terminalDeviceId ?? businessInfo?.stripe_terminal_device_id;
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("requires_payment_method");
  const [isPolling, setIsPolling] = useState(true);
  const isPollingRef = useRef(true); // Needed for live references to value for the polling interval
  const pollIntervalRef = useRef(null);
  const pollCountRef = useRef(0);
  const hasInitiatedRef = useRef(null); // Track which payment intent we've initiated to prevent duplicates
  const bnplMethod = bnplMethodForKey(paymentMethod);
  const isBnpl = !!bnplMethod;
  // Max polls before we stop checking (2s interval): ~2 min for card taps,
  // ~10 min for BNPL — the customer is completing a financing application
  // on their own phone, which routinely takes several minutes.
  const maxPolls = isBnpl ? 300 : 60;
  const [backendTips, setBackendTips] = useState(null);
  const [needsRefetch, setNeedsRefetch] = useState(false);

  const clearPollInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const updatePollingStatus = (status) => {
    isPollingRef.current = status;
    setIsPolling(status);
  };

  const startPolling = useCallback(async () => {
    if (!isPollingRef.current) {
      clearPollInterval();
      return;
    }

    try {
      const response = await pollStripePaymentStatus(
        transactionId,
        paymentIntentId,
      );

      if (response) {
        setPaymentStatus(response.status);

        if (response.status === "succeeded") {
          // Update transaction with payment info (backend already includes tip allocations)
          onTransactionUpdated(response);

          // Store tips data from backend if present for the "Allocate Tips" button
          if (response.tips) {
            setBackendTips(response.tips);
            // Mark that we should refetch when closing to get the latest tip data
            setNeedsRefetch(true);
          }
        }

        if (
          response.status === "succeeded" ||
          response.status === "canceled" ||
          response.status === null
        ) {
          updatePollingStatus(false);
          clearPollInterval();
          return;
        }
      }

      pollCountRef.current++;
      if (pollCountRef.current >= maxPolls) {
        updatePollingStatus(false);
        clearPollInterval();
      }
    } catch (err) {
      // The Stripe webhook writes the payment row asynchronously, so the
      // first few polls commonly race ahead of it and 404. Treat 404 as
      // "not yet" and keep polling until we hit the max-attempts ceiling.
      // Any non-404 error is fatal.
      if (err?.status === 404) {
        pollCountRef.current++;
        if (pollCountRef.current >= maxPolls) {
          updatePollingStatus(false);
          clearPollInterval();
        }
        return;
      }
      setError(err.message);
      updatePollingStatus(false);
      clearPollInterval();
    }
  }, [transactionId, paymentIntentId, clearPollInterval, onTransactionUpdated]);

  const initiatePayment = useCallback(async () => {
    if (!paymentIntentId || eligibleAmountForTip === null) return false;

    try {
      const response = await processPayment({
        terminalDeviceId: effectiveTerminalDeviceId,
        paymentIntentId: paymentIntentId,
        eligibleAmountForTip: eligibleAmountForTip,
        accountId: businessInfo.stripe_connect_account_id,
        // BNPL: backend omits the on-reader tipping config (QR methods
        // reject it) and the reader renders the provider's QR code.
        ...(bnplMethod
          ? { paymentMethodType: bnplMethod.paymentMethodType }
          : {}),
      });

      if (response.error) {
        setError(friendlyTerminalError(response.error));
        isPollingRef.current = false;
        return false;
      }
      return true;
    } catch (err) {
      setError(friendlyTerminalError(err.message));
      isPollingRef.current = false;
      return false;
    }
  }, [
    businessInfo,
    effectiveTerminalDeviceId,
    paymentIntentId,
    eligibleAmountForTip,
    bnplMethod,
  ]);

  const startProcess = useCallback(async () => {
    pollCountRef.current = 0;
    const success = await initiatePayment();
    if (success && isPollingRef.current) {
      pollIntervalRef.current = setInterval(startPolling, 2000);
    }
  }, [initiatePayment, startPolling]);

  /** Card-on-file workflow: poll only (charge already initiated server-side). */
  const startCardOnFileWorkflow = useCallback(() => {
    pollCountRef.current = 0;
    updatePollingStatus(true);
    clearPollInterval();
    startPolling();
    pollIntervalRef.current = setInterval(startPolling, 2000);
  }, [startPolling, clearPollInterval]);

  useEffect(() => {
    if (opened) {
      updatePollingStatus(true);
      pollCountRef.current = 0;
      setPaymentStatus(cardOnFile ? "processing" : "requires_payment_method");
      setError(null);
      setNeedsRefetch(false);
      hasInitiatedRef.current = null; // Reset on open
    } else {
      updatePollingStatus(false);
      clearPollInterval();
    }
  }, [opened, clearPollInterval, cardOnFile]);

  useEffect(() => {
    if (!paymentIntentId || !opened) return;

    // Only start the process if we haven't already initiated this payment intent
    if (hasInitiatedRef.current !== paymentIntentId) {
      hasInitiatedRef.current = paymentIntentId;
      if (cardOnFile) {
        startCardOnFileWorkflow();
      } else {
        startProcess();
      }
    }
    // Intentionally NO cleanup function: this effect's deps re-run on every
    // parent re-render (since startProcess/startPolling depend on the
    // onTransactionUpdated callback prop), and a cleanup here would kill the
    // poll interval mid-payment whenever the parent re-renders. The interval
    // is cleared by the `opened` toggle effect above and by the unmount
    // effect below.
  }, [
    paymentIntentId,
    opened,
    cardOnFile,
    startProcess,
    startCardOnFileWorkflow,
  ]);

  // Unmount-only cleanup: ensures we never leave a setInterval running after
  // this component goes away.
  useEffect(() => clearPollInterval, [clearPollInterval]);

  const renderStatusIcon = () => {
    switch (paymentStatus) {
      case "succeeded":
        return null;
      case "canceled":
      case null:
        return (
          <IconX
            size={60}
            color="red"
            style={{
              padding: "12px",
              borderRadius: "100%",
              border: "2px solid red",
              marginBottom: "16px",
            }}
          />
        );
      default:
        return <Loader size="xl" color="teal" />;
    }
  };

  const handleSimulation = async (cardNumber) => {
    try {
      const response = await simulatePayment({
        terminalDeviceId: effectiveTerminalDeviceId,
        cardNumber: cardNumber,
        accountId: businessInfo.stripe_connect_account_id,
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleClose = async () => {
    isPollingRef.current = false;
    if (
      !cardOnFile &&
      paymentStatus !== "succeeded" &&
      paymentStatus !== null &&
      paymentStatus !== "canceled"
    ) {
      try {
        let response = await cancelPaymentIntent({
          paymentIntentId: paymentIntentId,
          terminalDeviceId: effectiveTerminalDeviceId,
          accountId: businessInfo.stripe_connect_account_id,
        });
        if (response) {
          setPaymentStatus(response.status);
        }
      } catch (err) {
        console.error("Error canceling payment intent:", err);
      }
    }

    // If payment succeeded and we need to refetch (tips were applied/reallocated), trigger refetch
    const shouldRefetch = paymentStatus === "succeeded" && needsRefetch;

    // Reset state on close
    setBackendTips(null);
    setNeedsRefetch(false);

    // Call refetch callback if provided and needed
    if (shouldRefetch && onRefetchTransaction) {
      await onRefetchTransaction();
    }

    onClose(paymentStatus === "succeeded");
  };

  const renderHeader = () => {
    if (
      cardOnFile &&
      paymentStatus !== "succeeded" &&
      paymentStatus !== "canceled" &&
      paymentStatus !== null
    ) {
      return (
        <Text size="lg" ta="center">
          Processing payment...
        </Text>
      );
    }

    if (
      isBnpl &&
      paymentStatus !== "succeeded" &&
      paymentStatus !== "canceled" &&
      paymentStatus !== null
    ) {
      return (
        <Text size="lg" ta="center">
          {paymentStatus === "requires_payment_method"
            ? `Ask the customer to scan the QR code on the reader with their phone to pay with ${bnplMethod.label}.`
            : `The customer is completing their ${bnplMethod.label} application on their phone.`}
        </Text>
      );
    }

    switch (paymentStatus) {
      case "requires_payment_method":
      case "processing":
      case "requires_action":
      case "requires_confirmation":
        return (
          <Text size="lg" ta="center">
            Continue processing payment using the WisePOS E reader...
          </Text>
        );
      case "succeeded":
        return null;
      case "canceled":
      case null:
        return (
          <Text size="lg" ta="center">
            Payment canceled.
          </Text>
        );
      default:
        return (
          <Text size="lg" ta="center">
            Unexpected payment processing error.
          </Text>
        );
    }
  };

  const renderSubText = () => {
    if (isBnpl) {
      switch (paymentStatus) {
        case "requires_payment_method":
          return (
            <Text c="dimmed" fs="italic">
              Waiting for the customer to scan...
            </Text>
          );
        case "processing":
        case "requires_action":
        case "requires_confirmation":
          return (
            <Text c="dimmed" fs="italic">
              This can take a few minutes. Keep this window open.
            </Text>
          );
        default:
          break;
      }
    }

    switch (paymentStatus) {
      case "requires_payment_method":
        return (
          <Text c="dimmed" fs="italic">
            Waiting for payment method...
          </Text>
        );
      case "processing":
        if (cardOnFile) return null;
        return (
          <Text c="dimmed" fs="italic">
            Processing payment...
          </Text>
        );
      case "requires_action":
      case "requires_confirmation":
        return (
          <Text c="dimmed" fs="italic">
            Waiting for customer action...
          </Text>
        );
      case "succeeded":
        return null;
      case "canceled":
      case null:
        return null;
      default:
        return (
          <Text c="dimmed" fs="italic">
            If this issue persists, please contact support at (617) 237-6592 or
            contact@getpagoda.com.
          </Text>
        );
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title="Processing Payment"
        size="lg"
        centered
      >
        <Stack gap="xl" p="sm">
          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}

          {renderHeader()}

          {paymentStatus !== "succeeded" && (
            <Stack align="center" gap="md">
              {renderStatusIcon()}
              {renderSubText()}
            </Stack>
          )}

          {paymentStatus === "succeeded" && transaction && (
            <CardPaymentSuccessPanel
              paymentIntentId={paymentIntentId}
              customerEmail={customerEmail}
              transaction={transaction}
              backendTips={backendTips}
              onTipsChanged={() => setNeedsRefetch(true)}
            />
          )}

          {(paymentStatus === "requires_payment_method" ||
            paymentStatus === "processing" ||
            paymentStatus === "requires_action" ||
            paymentStatus === "requires_confirmation") && (
            <Text c="red" fw={700} ta="center">
              Please do not close or refresh your page.
            </Text>
          )}

          {!isPolling &&
            !error &&
            pollCountRef.current >= maxPolls &&
            paymentStatus !== "succeeded" &&
            paymentStatus !== "canceled" &&
            paymentStatus !== null && (
              <Button
                onClick={() => {
                  updatePollingStatus(true);
                  if (cardOnFile) {
                    startCardOnFileWorkflow();
                  } else if (
                    isBnpl &&
                    paymentStatus !== "requires_payment_method"
                  ) {
                    // The customer is mid-application on their phone —
                    // re-driving the reader would fail with
                    // intent_invalid_state, so resume polling only.
                    startCardOnFileWorkflow();
                  } else {
                    // Card, or BNPL where the QR was never scanned: safe to
                    // re-hand-off (re-displays the QR on the reader).
                    startProcess();
                  }
                }}
                fullWidth
              >
                {cardOnFile || isBnpl
                  ? "Continue checking payment status"
                  : "Continue checking reader status"}
              </Button>
            )}

          {/* For testing purposes only, shouldn't show up in production.
              Hidden for BNPL: Stripe's present_payment_method test helper is
              card_present-only and can't simulate a QR scan. */}
          {process.env.REACT_APP_SHOW_PAYMENT_SIMULATION === "true" &&
            !cardOnFile &&
            !isBnpl &&
            paymentStatus !== "succeeded" &&
            paymentStatus !== "canceled" &&
            paymentStatus !== null && (
              <Group grow>
                <Button
                  onClick={() => handleSimulation("4242424242424242")}
                  color="green"
                  disabled={
                    error ||
                    paymentStatus === "succeeded" ||
                    paymentStatus === "canceled" ||
                    paymentStatus === null
                  }
                >
                  Simulate Success
                </Button>
              </Group>
            )}
        </Stack>
      </Modal>
    </>
  );
}

export default PaymentProcessingModal;
